import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import { productsApi } from '@mssgme/lib';
import { __t, MessengersBlockKind } from '@mssgme/shared';
import { useNavigationUtils } from '@mssgme/hooks';
import { usePresenceRemoval } from '@mssgme/ui';
import { ServiceContent, ThemedModal } from '../../UI';
import { useCRMData, useModalThemes, useWebsiteData } from '../../hooks';

const addUrl = (product, finalUrl) => {
    const text = encodeURIComponent(
        `${__t('buy_template')} ${product.title || product.name} - ${finalUrl}/p/${product.slug}`
    );
    const ref = `buy_${product._id}_${Date.now()}_${document.documentElement.lang}`;
    const params = {
        text,
        ref,
        start: ref,
    };

    return (messenger) => {
        return { ...messenger, params };
    };
};

export function ProductView({ workspaceId, defaultCollectionId, productSlug, interactive = true, ...props }) {
    const history = useHistory();
    const fromCollection = history.location.state?.fromCollection || defaultCollectionId;
    const { generateUrl } = useNavigationUtils();
    const { contentStyle } = useModalThemes();
    const { finalUrl } = useWebsiteData();
    const { contacts } = useCRMData();
    const backPath = useMemo(() => generateUrl('../..'), [history.location]);

    const [{ value: product, error }, getProduct] = useAsyncFn(
        () => productsApi.loadBySlug(workspaceId, productSlug, fromCollection),
        [workspaceId, productSlug, fromCollection]
    );
    const messengers = useMemo(
        () =>
            product && contacts.messengers.length
                ? [
                    {
                        kind: MessengersBlockKind,
                        messengers: contacts.messengers.map(addUrl(product, finalUrl)),
                        enabled: true,
                    },
                ]
                : [],
        [contacts.messengers, product, finalUrl]
    );
    const is404 = error?.status === 404;

    const handleClose = useCallback(() => history.push(backPath), [backPath]);

    useEffect(() => {
        if (fromCollection) {
            void getProduct();
        }
    }, [productSlug, fromCollection]);

    usePresenceRemoval();

    return (
        <ThemedModal overlay showHeader={!!error} title={error ? __t('error') : ''} onClose={handleClose}>
            {(handleClose) =>
                error ? (
                    <div style={{ padding: 16, display: 'flex' }}>
                        {is404 ? __t('product_not_found') : __t('no_products')}
                    </div>
                ) : product ? (
                    <ServiceContent
                        {...props}
                        themeStyles={contentStyle}
                        service={product}
                        contacts={messengers}
                        isActive
                        isPrev={false}
                        isNext={false}
                        interactive={interactive}
                        backUrl={backPath}
                        onClose={handleClose}
                    />
                ) : null
            }
        </ThemedModal>
    );
}

ProductView.propTypes = {
    productSlug: PropTypes.string.isRequired,
    workspaceId: PropTypes.string.isRequired,
    defaultCollectionId: PropTypes.string.isRequired,
    interactive: PropTypes.bool,
};
